import { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import searchIcon from "../../assets/search_icon.png";
import CustomButton from "../../components/CustomButton";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import BalanceBoxes from "../../components/BalanceBoxes";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import { UserAuth } from "../../utils/AuthContext";
import CustomInput from "../../components/CustomInput";
import SearchFilter from "../../components/SearchFilter";
import InformationBox from "../../components/InformationBox";
import useLanguage from "../../utils/useLanguage";

import downloadIcon from "../../assets/download-icon.svg";
import { pdf } from "@react-pdf/renderer";
import PageWrapper from "../../components/PageWrapper";
import ErrorBox from "../../components/ErrorBox";
import RevenuesTemplate from "../../components/RevenuesTemplate";
import axiosClient from "../../services/axiosClient";

// const response = {
//   status: 200,
//   data: {
//     revenues: [
//       {
//         id: "31d11286-1a90-4832-83e6-e01993b67d24",
//         receiptNumber: "wia3m",
//         kassaId: "6091dbad-c998-4ccf-b770-357aefb23eb9",
//         studentPaymentId: null,
//         productOrderId: null,
//         type: "SELL",
//         date: "2026-12-01T00:00:00.000Z",
//         notes: null,
//         amount: 100,
//         createdAt: "2025-10-25T07:08:05.333Z",
//         kassa: {
//           name: "Kassa 1",
//           organization: {
//             organizationName: "Organization 1",
//           },
//         },
//       },
//       {
//         id: "2636d1c6-98b7-4f80-9988-6f8ab585ca31",
//         receiptNumber: "nw83o",
//         kassaId: "6091dbad-c998-4ccf-b770-357aefb23eb9",
//         studentPaymentId: null,
//         productOrderId: null,
//         type: "SELL",
//         date: "2025-10-25T07:12:22.323Z",
//         notes: null,
//         amount: 20,
//         createdAt: "2025-10-25T07:12:41.211Z",
//         kassa: {
//           name: "Kassa 1",
//           organization: {
//             organizationName: "Organization 1",
//           },
//         },
//       },
//     ],
//     balance: 0,
//     currentPage: 1,
//     totalPages: 1,
//     totalRecords: 0,
//   },
// };

const RevenuesPage = () => {
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const { currentDirection } = useLanguage();

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  const [selectedTitle, setSelectedTitle] = useState({
    title: t("Date"),
    value: "date",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");

  const [revenues, setRevenues] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState("");

  // CONSTANTS
  const searchTitles = [
    { title: t("Date"), value: "date" },
    { title: t("amount"), value: "amount" },
    { title: t("kassa"), value: "kassa" },
  ];

  const handleDownloadRevenuesPDF = async () => {
    setPdfLoading(true);

    try {
      const filterParams = {
        page: 1,
        limit: 100000,
        startDate,
        endDate,
        minAmount: minAmount ? parseFloat(minAmount) : undefined,
        maxAmount: maxAmount ? parseFloat(maxAmount) : undefined,
        kassaName: searchQuery,
        searchField: selectedTitle?.value,
      };

      const response = await axiosClient.get("/kassaTransaction/income", {
        params: filterParams,
      });
      const organizationName = response.data?.organizationName;
      // Generate the PDF with your custom component
      const blob = await pdf(
        <RevenuesTemplate
          language={currentDirection === "rtl" ? "AR" : "EN"}
          companyName={
             organizationName 
          }
          revenues={response.data.kassaTransactions}
          schoolName={organizationName}
        />
      ).toBlob();

      // Trigger browser download
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${t("revenues")}-${
        new Date().getMonth() + 1
      }-${new Date().getDate()}-${(Math.random() * 1000).toFixed(0)}.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 5000);
      document.body.removeChild(link);
    } catch (error) {
      console.error("PDF generation failed:", error);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleSearchType = (type) => {
    setSelectedTitle(type);
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    setMinAmount("");
    setMaxAmount("");
  };

  const getRevenues = async (page = 1, limit = 10) => {
    setLoading(true);
    setError("");

    try {
      const filterParams = {
        page,
        limit,
        startDate,
        endDate,
        minAmount: minAmount ? parseFloat(minAmount) : undefined,
        maxAmount: maxAmount ? parseFloat(maxAmount) : undefined,
        kassaName: searchQuery,
        searchField: selectedTitle?.value,
      };

      const response = await axiosClient.get("/kassaTransaction/income", {
        params: filterParams,
      });

      setBalance(response.data.totalIncome);

      setPagination({
        currentPage: response.data.currentPage || page,
        totalPages: response.data.totalPages || 1,
        totalRecords: response.data.totalRecords || 0,
      });

      const revenues = response.data.kassaTransactions || [];

      setRevenues(revenues);
    } catch (error) {
      console.error("Failed to fetch revenues data:", error);
      setError("errorGettingRevenues");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    getRevenues();
  };

  const handlePaginationChange = (newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
    getRevenues(newPage);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    setMinAmount("");
    setMaxAmount("");
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    getRevenues(1);
  };

  const renderSearchInput = () => {
    switch (selectedTitle.value) {
      case "date":
        return (
          <div className="o-column-row-switcher o-column--gap-size_300">
            <CustomInput
              name="startDate"
              title={t("from")}
              type="date"
              thick
              onChange={(e) => setStartDate(e.target.value)}
              resetSearch={() => setStartDate("")}
              value={startDate}
            />
            <CustomInput
              name="endDate"
              title={t("to")}
              type="date"
              thick
              onChange={(e) => setEndDate(e.target.value)}
              resetSearch={() => setEndDate("")}
              value={endDate}
            />
          </div>
        );
      case "amount":
        return (
          <div className="o-column-row-switcher o-column--gap-size_300">
            <CustomInput
              name="minAmount"
              title={t("minAmount")}
              type="number"
              placeholder={t("minAmount")}
              thick
              onChange={(e) => setMinAmount(e.target.value)}
              resetSearch={() => setMinAmount("")}
              value={minAmount}
              flex
            />
            <CustomInput
              name="maxAmount"
              title={t("maxAmount")}
              type="number"
              placeholder={t("maxAmount")}
              thick
              onChange={(e) => setMaxAmount(e.target.value)}
              resetSearch={() => setMaxAmount("")}
              value={maxAmount}
              flex
            />
          </div>
        );

      default:
        return (
          <CustomInput
            placeholder={`${t("searchTitle")} ${t(selectedTitle?.value)}`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            resetSearch={handleClearSearch}
            thick
            flex
          />
        );
    }
  };

  useEffect(() => {
    getRevenues(pagination.currentPage);
  }, [pagination.currentPage]);

  const tableTitles = [
    { value: "kassa", keepOnMobile: true },
    { value: "receiptNumber", keepOnMobile: true },
    { value: "amount", keepOnMobile: true },
    { value: "Date", keepOnMobile: true },
  ];

  const tableRows = revenues?.map((transaction) => [
    {
      value: transaction.kassa.name,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: transaction.receiptNumber,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: transaction.amount,
      type: TABLE_CELL_TYPES.MONEY,
      sign: "positive-value",
      bold: true,
      keepOnMobile: true,
      currency: t("IQD"),
    },
    {
      value: transaction.date,
      type: TABLE_CELL_TYPES.DATE,
      keepOnMobile: true,
    },
  ]);

  return (
    <PageWrapper
      style={{ direction: currentDirection }}
      title={t("revenues")}
      withBackButton
    >
      {/* Search Section */}
      <div className="o-column o-column--gap-size_300 u-margin--top_700">
        <label className="t-bold_semi-bold">
          {`${t("searchTitle")} ${t(selectedTitle?.title)}`}
        </label>
        <section className="o-column-row-switcher o-row--gap-size_300 o-column--vertical-align_end u-margin--bottom_500">
          <div className="o-row o-row--vertical-align_center o-column--vertical-align_end o-row--gap-size_300 o-column--horizontal-align_start">
            {renderSearchInput()}
            <SearchFilter
              options={searchTitles}
              selected={selectedTitle}
              setSelected={handleSearchType}
              thick
            />
          </div>

          <CustomButton
            title={t("search")}
            onClick={handleSearch}
            icon={searchIcon}
            iconSize={15}
            classList="c-search-button"
          />
        </section>
      </div>

      {loading && <Loader size={LOADER_SIZES.MEDIUM} />}

      {error && <ErrorBox>{t(error)}</ErrorBox>}

      {!error && !loading && revenues?.length > 0 && (
        <>
          <section>
            <RoundedTable
              titles={tableTitles}
              rows={tableRows}
              pagination={pagination}
              setPagination={setPagination}
              onPaginationChange={handlePaginationChange}
            />
            {pdfLoading && <Loader />}
            <div className="o-row o-row--horizontal-align_end u-margin--top_500 u-margin--bottom_400">
              <CustomButton
                variant="blank"
                onClick={handleDownloadRevenuesPDF}
                disabled={pdfLoading}
                icon={downloadIcon}
                title={pdfLoading ? t("generatingPdf") : t("downloadStatement")}
              />
            </div>
          </section>
        </>
      )}

      {!error && !loading && revenues?.length === 0 && (
        <InformationBox>{t("noRevenues")}</InformationBox>
      )}

      {/* Balance Box */}
      <BalanceBoxes
        boxes={[
          {
            title: "balance",
            value: balance,
            currency: t("IQD"),
          },
        ]}
      />
    </PageWrapper>
  );
};

export default RevenuesPage;
